'use client'

import { ImageMedia } from '@/components/Media/ImageMedia'
import { Property } from '@/payload-types'
import { useTheme } from '@/providers/Theme'
import { formatPriceDZD } from '@/utilities/PriceFormat'
import { cn } from '@/utilities/ui'
import MuxPlayer from '@mux/mux-player-react'
import { sendGTMEvent } from '@next/third-parties/google'
import { BedSingle, Camera, ChevronLeft, ChevronRight, Phone, Video } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import React from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import Slider from 'react-slick'

const NextArrow = ({ onClick }: { onClick?: () => void }) => (
  <div
    onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      onClick?.(e)
    }}
    className="absolute top-1/2 right-2 -translate-y-1/2 z-20 bg-white/70 hover:bg-white p-1 rounded-full shadow cursor-pointer"
  >
    <ChevronRight size={18} />
  </div>
)

const PrevArrow = ({ onClick }: { onClick?: () => void }) => (
  <div
    onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      onClick?.(e)
    }}
    className="absolute top-1/2 left-2 -translate-y-1/2 z-20 bg-white/70 hover:bg-white p-1 rounded-full shadow cursor-pointer"
  >
    <ChevronLeft size={18} />
  </div>
)


const PropertyCard = ({ property, dictionary, lang }: { property: Property, dictionary: any, lang: string }) => {
  const { theme } = useTheme()

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: 'ease-out',
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    appendDots: (dots: React.ReactNode) => (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ bottom: "20px" }}
        className="absolute w-full flex justify-center z-20"
      >
        <ul className="flex justify-center">{dots}</ul>
      </div>
    ),
    customPaging: () => (
      <div className="w-2 h-2 bg-white/70 rounded-full hover:bg-white" />
    ),
  }

  const mediaItems = [
    ...(property?.mediaSection?.images || []).map((img) => ({
      type: 'image',
      url: img?.image?.url,
      image: img?.image,
    })),
    ...(property?.mediaSection?.videos || []).map((vid) => ({
      type: 'video',
      playbackId: vid?.video?.mux_playback_id,
    })),
  ]

  const handlePhoneClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    
    sendGTMEvent({
      event: 'phone_click',
      phone_number: property?.agency?.phone,
      property_id: property?.id,
    })

    window.open(`tel:${property?.agency?.phone}`)
  }

  const handleWhatsappClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()

    sendGTMEvent({
      event: 'whatsapp_click',
      whatsapp_number: property?.agency?.contactSection?.whatsapp,
      property_id: property?.id,
    })

    window.open(
      `https://wa.me/${property?.agency?.contactSection?.whatsapp}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const router = useRouter()

  return (
    <Link
      href={`/properties/${property?.slug}`}
      key={property.id}
      className="bg-card group rounded-xl overflow-hidden shadow-sm flex flex-col sm:flex-row"
    >
      <div className="relative w-full h-64 sm:w-1/3 overflow-hidden">
         {mediaItems.length > 0 ? (
          mediaItems.length === 1 ? (
            mediaItems[0]?.type === 'image' ? (
              <ImageMedia
                resource={mediaItems[0].image}
                imgClassName="object-cover"
                alt={property?.title || 'Property media'}
                loading="eager"
                priority
                fill
                size="(max-width: 768px) 100vw, 50vw"
              />
            ) : (
              <MuxPlayer
                playbackId={mediaItems[0].playbackId}
                accentColor="#006633"
                className="object-cover w-full h-full"
                playsInline
                muted
              />
            )
          ) : (
            <Slider {...sliderSettings} className="h-full">
              {mediaItems.slice(0, 6).map((item, index) =>
                item.type === 'image' ? (
                  <div key={index} className="h-64 w-full relative">
                    <ImageMedia
                      resource={item.image}
                      imgClassName="object-cover"
                      fill
                      size="(max-width: 768px) 100vw, 50vw"
                      alt={property?.title + '-' + index || 'Property image'}
                      priority={index < 3}
                      loading={index < 3 ? 'eager' : 'lazy'}
                    />
                  </div>
                ) : (
                  <div key={index} className="h-64 w-full relative" onClick={(e) => e.stopPropagation()}>
                    <MuxPlayer
                      playbackId={item.playbackId}
                      accentColor="#006633"
                      className="object-cover w-full h-full"
                      playsInline
                      muted
                    />
                  </div>
                )
              )}
            </Slider>
          )
        ) : (
          <div className="h-64 w-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-sm">No media available</span>
          </div>
        )}
        
        {property?.propertyDescription?.price && (
          <div className="absolute top-3 left-3 bg-white py-1 px-3 rounded-md text-sm font-semibold">
            {formatPriceDZD(property?.propertyDescription?.price, lang)}
          </div>
        )}
      </div>
      <div className="px-[30px] py-5 w-full sm:w-2/3 flex flex-col">
        <div className="flex flex-col gap-2 relative">
          <div className="md:block hidden absolute ltr:right-0 rtl:left-0">
            {property?.agency?.Media?.url && (
              <div  
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    router.push(`/agencies/${property?.agency?.slug}`)
                  }} 
                  className="w-[70px] h-[70px] rounded-full bg-white flex items-center justify-center overflow-hidden hover:scale-105 transition-transform duration-300">
                <Image
                  src={property?.agency?.Media?.url}
                  alt='Agency logo'
                  width={70}
                  height={70}
                  priority
                  loading="eager"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
          </div>
          <div className="max-w-[85%]">
            <h3 className="text-typography text-xl font-bold truncate">{property?.title}</h3>
          </div>
          <p className="text-sm text-descriptionColor mb-2">{property?.locationSection?.address}</p>
        </div>

        <div className="flex-grow border-b border-b-border pb-2">
          <div className="flex items-center gap-8 mt-3">
            {property?.propertyDescription?.category !== 'land' && (
              <div className="text-typography flex items-center gap-2">
                <span className="inline-block">
                  <BedSingle size={20} className="stroke-1" />
                </span>
                <span className="text-sm">{property?.detailSection?.rooms || 0}</span>
              </div>
            )}

            {property?.detailSection?.surface && (
              <div className="text-typography flex items-center gap-2">
                {theme === 'dark' ? (
                  <img src="/icons/expand-w.svg" alt="w expand" className="" />
                ) : (
                  <img src="/icons/expand.svg" alt="expand" className="" />
                  )}
                <span className="text-sm">{property?.detailSection?.surface} m²</span>
              </div>
            )}
          </div>

          <p
            className="text-typography text-sm mt-3 leading-6 line-clamp-2"
            style={{
              letterSpacing: '0%',
            }}
          >
            {property?.propertyDescription?.description}
          </p>
        </div>

        <div className="flex justify-between items-center mt-4 h-[35px] ">
          <div className="flex gap-2">
            <div className={cn('h-full flex items-center text-sm px-2 py-2 rounded-md gap-1', theme === 'dark' ? 'text-white' : '')}>
              <span className="ml-1">{property?.mediaSection?.images?.length || 0}</span>
              <Camera size={16} />
            </div>

            {property?.mediaSection?.videos && (
              <div className={cn('h-full flex items-center text-sm px-2 py-2 rounded-md gap-1', theme === 'dark' ? 'text-white' : '')}>
                <span className="ml-1">{property?.mediaSection?.videos?.length || 0}</span>
                <Video size={16} />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* PHONE */}
            {property?.agency?.phone && (
              <div
                className="w-24 text-typography hover:text-black flex rtl:flex-row-reverse items-center justify-center gap-2 border border-gray-500 rounded-lg p-2 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={handlePhoneClick}
              >
                <Phone size={16} />
                <span className="text-sm font-semibold">
                  {dictionary?.call || 'Call'}
                </span>
              </div>
            )}
            
            {/* WHATSAPP */}
            {property?.agency?.contactSection?.whatsapp && (
              <div
                className="w-24 text-typography hover:text-black flex items-center justify-center gap-2 border !border-[#25D366] rounded-lg p-2 !bg-[#25D366] transition-colors cursor-pointer"
                onClick={handleWhatsappClick}
              >
                <FaWhatsapp size={20} color="#fff" />
              </div>
            )}
            
          </div>
        </div>
      </div>
    </Link>
  )
}

export default PropertyCard
